import { useState, useEffect } from 'react'
import { FileText, Download, Calendar, Loader2, ClipboardList } from 'lucide-react'
import { api, Report } from '../services/api'
import { toast } from 'sonner'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

export function ReportsSection() {
  const [report, setReport] = useState<Report | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    loadReport()
  }, [])

  const loadReport = async () => {
    setIsLoading(true)
    try {
      const data = await api.getReport()
      setReport(data)
    } catch (error) {
      console.error('Erro ao carregar relatório:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownload = async () => {
    if (!report) return
    setDownloading(true)
    try {
      await api.downloadReport(report.id, report.originalName)
      toast.success(`Download iniciado: ${report.title}`)
    } catch (error) {
      toast.error('Erro ao baixar o arquivo')
    } finally {
      setDownloading(false)
    }
  }

  if (isLoading) {
    return (
      <section id="relatorios" className="py-16 md:py-20 bg-white">
        <div className="container text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mb-4"></div>
          <p className="text-gray-500">Carregando relatório...</p>
        </div>
      </section>
    )
  }

  return (
    <section id="relatorios" className="py-16 md:py-20 bg-white">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">
            Participação Social
          </span>
          <h2 id="relatorios-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Relatório da Escuta Social
          </h2>
          <p className="text-gray-600">
            Acompanhe os resultados da escuta social realizada com a população sobre o projeto.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {!report ? (
            <div className="bg-[#F5F7FA] rounded-xl p-12 text-center border border-gray-100">
              <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Nenhum relatório disponível no momento.</p>
              <p className="text-sm text-gray-400 mt-2">
                O relatório da Escuta Social será publicado em breve.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-12 h-12 bg-[#51A64D]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText size={22} className="text-[#51A64D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{report.title}</h3>
                  {report.description && (
                    <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(report.createdAt)}
                    </span>
                    <span>•</span>
                    <span>{formatFileSize(report.fileSize)}</span>
                    <span>•</span>
                    <span className="uppercase">PDF</span>
                  </div>
                </div>
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white text-sm font-medium rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-60 flex-shrink-0"
                >
                  {downloading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Baixando...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Baixar
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
